import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import Box from 'components/atoms/Layout/Box/Box';
import StyledConversationCard from 'components/organisms/Cards/ConversationCard/ConversationCard.styles';
import Markdown from 'components/atoms/Formatters/Markdown/Markdown';
import ScoreLinearProgress from 'components/organisms/Progress/ScoreLinearProgress/ScoreLinearProgress';
import constants from 'helpers/constants';
import Typography from 'components/atoms/Text/Typography/Typography';
import ActionCard from 'components/organisms/Cards/ActionCard/ActionCard';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ActionButton from 'components/molecules/Buttons/ActionButton/ActionButton';

const ConversationCard = React.forwardRef((props, ref) => {
  const {
    message,
    onApply,
    onPage,
    autoApply,
    isLoading,
    ...innerProps
  } = useComponentProps({
    elevation: 0,
    ...props
  }, 'ConversationCard', {
    static: ['action'],
    variable: ['role']
  });

  const actionRef = useRef(null);
  const [internalState, setInternalState] = useState({
    selectedPage: 0,
    answers: {}
  });

  const isActionCard = message.actions?.length > 0;
  const isQuestionAction = [constants.assistant.adviceActionTypes.askQuestion, constants.assistant.adviceActionTypes.needMoreInfo].includes(message.actions?.[0]?.id);
  const actionPageCount = isQuestionAction ? message.actions?.[0]?.details?.length : message.actions?.length;
  const appliedPageCount = isQuestionAction ? 0 : message.actions?.filter((a) => a.applied).length;
  const currentAction = isQuestionAction ? message.actions?.[0] : message.actions?.[internalState.selectedPage];
  const nextActionIdx = isQuestionAction ? null : Math.max(
    message.actions?.findIndex((a, idx) => idx > internalState.selectedPage && !a.applied),
    message.actions?.findLastIndex((a, idx) => idx < internalState.selectedPage && !a.applied)
  );

  const [answer, answerValid] = useMemo(() => {
    const requiredCount = utils.toArray(currentAction?.details).filter((q) => q?.required ?? true).length;

    const answer = Object.keys(internalState.answers).reduce((a, key) => {
      // check for empty other answers
      let answer = utils.toArray(internalState.answers[key]).filter((a) => (a?.label ?? a)?.length > 0);

      if (answer.length > 0) {
        a[key] = utils.isArray(internalState.answers[key]) ? answer : answer[0];
      } else {
        a[key] = null;
      }

      return a;
    }, []);

    const valid = requiredCount === 0 ||
      answer.filter((a, idx) => utils.isDefined(a) && (currentAction?.details[idx]?.required ?? true)).length > 0;

    return [answer, valid];
  }, [currentAction, internalState.answers]);

  const handleActionSubmit = useCallback((answer) => {
    setInternalState((current) => ({
      ...current,
      submitting: true,
      answers: {
        ...current.answers,
        [current.selectedPage]: answer
      }
    }));
  }, []);

  const handleActionChange = useCallback((answer) => {
    setInternalState((current) => ({
      ...current,
      answers: {
        ...current.answers,
        [current.selectedPage]: answer
      }
    }));
  }, []);

  const onApplyEvent = useEffectEvent(onApply);
  const onPageEvent = useEffectEvent(onPage);
  const [leftActions, rightActions] = useMemo(() => {
    const leftActions = [], rightActions = [];

    if (isActionCard) {
      const isBusyOrDone = Boolean(internalState.submitting || currentAction?.applying || currentAction?.applied);

      if (actionPageCount > 1) {
        leftActions.push({
          tooltip: 'Previous',
          icon: ChevronLeft,
          IconButtonProps: {
            disabled: isLoading || internalState.selectedPage <= 0 || (isQuestionAction && isBusyOrDone)
          },
          onClick: () => {
            setInternalState((current) => ({
              ...current,
              selectedPage: current.selectedPage - 1
            }));

            onPageEvent?.();
          }
        });

        leftActions.push({
          tooltip: 'Next',
          icon: ChevronRight,
          IconButtonProps: {
            disabled: isLoading || internalState.selectedPage >= (actionPageCount - 1) || (isQuestionAction && isBusyOrDone)
          },
          onClick: () => {
            setInternalState((current) => ({
              ...current,
              selectedPage: current.selectedPage + 1
            }));

            onPageEvent?.();
          }
        });
      }

      rightActions.push({
        label: (actionPageCount - appliedPageCount) > 1 ? 'Skip all' : 'Skip',
        ButtonProps: {
          variant: 'text',
          disabled: isLoading || isBusyOrDone
        },
        onClick: () => {
          onApplyEvent?.(currentAction, (actionPageCount - appliedPageCount) > 1 ? 'Skip all' : 'Skip');
        }
      });

      if (isQuestionAction) {
        const isSubmit = internalState.selectedPage >= (actionPageCount - 1);
        rightActions.push({
          label: isSubmit ? 'Apply' : 'Next',
          ButtonProps: {
            disabled: isLoading || (isSubmit && !answerValid) || isBusyOrDone
          },
          onClick: () => {
            actionRef.current?.refs?.formRef?.current?.submit();
          }
        });
      } else {
        const applyAction = (auto) => {
          onApplyEvent?.(currentAction, null, auto);

          setInternalState((current) => ({
            ...current,
            applyingPage: current.selectedPage
          }));
        };

        rightActions.push({
          label: currentAction?.applying ? 'Applying' : (currentAction?.applied ? 'Applied' : 'Apply'),
          ButtonProps: {
            disabled: isLoading || isBusyOrDone
          },
          onClick: () => {
            applyAction(false);
          }
        });

        if (autoApply && !(isLoading || isBusyOrDone)) {
          if (constants.data.lookup('assistantAdviceActions', currentAction.id)?.auto) {
            applyAction(true);
          }
        }
      }
    }

    return [leftActions, rightActions];
  }, [internalState.submitting, internalState.selectedPage, currentAction, isActionCard, autoApply, isLoading,
    actionPageCount, appliedPageCount, isQuestionAction, answerValid, onApplyEvent, onPageEvent]);

  const renderAction = () => {
    return <ActionCard ref={actionRef}
                       fullWidth={true}
                       action={currentAction}
                       isLoading={isLoading}
                       answers={internalState.answers}
                       pageIdx={internalState.selectedPage}
                       pageCount={actionPageCount}
                       onChange={handleActionChange}
                       onSubmit={handleActionSubmit}/>
  }

  const renderActions = () => {
    if (message?.actions?.length > 0) {
      return <Box className="ConversationCard-actions">
        <Box className="ConversationCard-actions-content">
          {renderAction()}
        </Box>
        <Box className="ConversationCard-actions-footer">
          <Box className="ConversationCard-actions-footer-left">
            {leftActions.map((action, idx) => {
              return <ActionIconButton key={idx}
                                       action={action}
                                       size="smaller"
                                       density="normal"
                                       variant="outlined"
                                       IconProps={{
                                         size: 'smaller'
                                       }}/>
            })}
          </Box>
          <Box className="ConversationCard-actions-footer-right">
            {rightActions.map((action, idx) => {
              return <ActionButton key={idx}
                                   action={action}
                                   size="small"/>
            })}
          </Box>
        </Box>
      </Box>
    }
  }

  const renderProgress = () => {
    if (utils.isDefined(message?.info?.progress) ||
        (message?.role === constants.assistant.conversation.roles.system && message?.related?.active)) {
      return <ScoreLinearProgress size="small"
                                  color="success"
                                  className="ConversationCard-progress"
                                  score={message?.info?.progress ?? message?.related?.progress?.total ?? 0}
                                  showEmpty={true} />
    }
  }

  useEffect(() => {
    if (isActionCard) {
      if (internalState.applyingPage && !currentAction?.applying) {
        setInternalState(utils.updater({
          applyingPage: null,
          submitting: false
        }, true));

        actionRef.current?.refs?.formRef.current?.submitting?.(false);
      } else if (!isQuestionAction) {
        if (internalState.selectedPage === internalState.applyingPage) {
          if (currentAction?.applied) {
            setInternalState((current) => ({
              ...current,
              applyingPage: null,
              selectedPage: nextActionIdx !== -1 ? nextActionIdx : current.selectedPage
            }));

            onPageEvent?.();
          }
        }
      } else { // isQuestionAction
        if (internalState.submitting) {
          if (internalState.applyingPage !== internalState.selectedPage && !currentAction.applying && !currentAction.applied) {
            if (internalState.selectedPage >= (actionPageCount - 1)) {
              if (answerValid) {
                onApplyEvent?.(currentAction, answer);
                setInternalState((current) => ({
                  ...current,
                  applyingPage: current.selectedPage
                }));
              } else {
                setInternalState((current) => ({
                  ...current,
                  submitting: false
                }));
                actionRef.current?.refs?.formRef.current?.submitting?.(false);
              }
            } else {
              setInternalState((current) => ({
                ...current,
                submitting: false,
                selectedPage: current.selectedPage + 1
              }));

              actionRef.current?.refs?.formRef.current?.submitting?.(false);
              onPageEvent?.();
            }
          }
        }
      }
    }
  }, [isActionCard, isQuestionAction, actionPageCount,
    currentAction, answer, answerValid, onApplyEvent, onPageEvent, nextActionIdx,
    internalState.submitting, internalState.selectedPage, internalState.applyingPage, internalState.answers]);

  innerProps.className = utils.flattenClassName(innerProps.className, {
    role: message?.role,
    action: message?.actions?.length > 0
  });

  return <StyledConversationCard ref={ref} {...innerProps} isLoading={isLoading}>
    <Box className="ConversationCard-content">
      {message.message ? <Typography as="div" className="ConversationCard-message" isLoading={isLoading} variant="body2">
        <Markdown>{message.message}</Markdown>
      </Typography> : null}
      {message?.info ? <Typography as="div" className="ConversationCard-info" isLoading={isLoading} variant="caption">
          <Markdown color={message.info.color}
                    isLoading={isLoading}>{message.info.message}</Markdown>
      </Typography> : null}
      {renderProgress()}
      {renderActions()}
    </Box>
    {!isLoading ? <Box className="ConversationCard-actions">
    </Box> : null}
  </StyledConversationCard>
});

ConversationCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  message: PropTypes.object,
  content: PropTypes.any,
  isLoading: PropTypes.bool,
  autoApply: PropTypes.bool,
  onApply: PropTypes.func,
  onPage: PropTypes.func
};

export default ConversationCard;
